import React, { useState, useEffect } from "react";
import { 
  Sparkles, 
  Scale, 
  CheckSquare, 
  Calendar, 
  Compass, 
  Users, 
  Heart, 
  Flame, 
  Droplet, 
  BookOpen, 
  TrendingUp,
  Award,
  Info,
  ChevronRight,
  Clipboard,
  Check,
  FileText,
  ClipboardList
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { PracticeSession } from "../types";

// Types for Sankalpa
interface SankalpaIntention {
  id: string;
  name: string;
  sanskritName: string;
  iastName: string;
  meaning: string;
  sanskritSankalpa: string;
  iastSankalpa: string;
  englishSankalpa: string;
  phrases: Array<{ sanskrit: string; iast: string; english: string }>;
}

const INTENTIONS: SankalpaIntention[] = [
  {
    id: "health",
    name: "Vital Health & Longevity",
    sanskritName: "आरोग्यं तथा बलवर्धनम्",
    iastName: "Ārogyaṃ tathā balavardhanam",
    meaning: "Strengthening the physical body and replenishing life force energy (Prāṇa).",
    sanskritSankalpa: "मम कायिक-वाचिक-मानसिक रोगनिवारणार्थं पूर्ण-आरोग्य-प्राप्त्यर्थं प्राणशक्ति-संवर्धनार्थं च",
    iastSankalpa: "mama kāyika-vācika-mānasika roganivāraṇārthaṃ pūrṇa-ārogya-prāptyarthaṃ prāṇaśakti-saṃvardhanārthaṃ ca",
    englishSankalpa: "for the removal of my physical, verbal, and mental ailments, and for the attainment of perfect health and vital energy",
    phrases: [
      { sanskrit: "मम", iast: "mama", english: "for my / on my behalf" },
      { sanskrit: "कायिक-वाचिक-मानसिक", iast: "kāyika-vācika-mānasika", english: "physical, verbal, and mental" },
      { sanskrit: "रोगनिवारणार्थम्", iast: "roganivāraṇārthaṃ", english: "for the removal of ailments" },
      { sanskrit: "पूर्ण-आरोग्य-प्राप्त्यर्थम्", iast: "pūrṇa-ārogya-prāptyarthaṃ", english: "for the attainment of perfect health" },
      { sanskrit: "प्राणशक्ति-संवर्धनार्थं च", iast: "prāṇaśakti-saṃvardhanārthaṃ ca", english: "and for the enhancement of life force" }
    ]
  },
  {
    id: "peace",
    name: "Mental Clarity & Serenity",
    sanskritName: "चित्तशुद्धिः तथा शान्तिः",
    iastName: "Cittaśuddhiḥ tathā śāntiḥ",
    meaning: "Quieting mental chatter, cultivating inner calm, and cleansing the subtle mind.",
    sanskritSankalpa: "मम अन्त:करण-शुद्ध्यर्थं चित्त-स्थैर्य-प्राप्त्यर्थं मानसिक-क्लेश-निवारणार्थं च",
    iastSankalpa: "mama antaḥkaraṇa-śuddhyarthaṃ citta-sthairya-prāptyarthaṃ mānasika-kleśa-nivāraṇārthaṃ ca",
    englishSankalpa: "for the purification of my inner psyche, the attainment of mental stability, and the complete dissolution of psychological stress",
    phrases: [
      { sanskrit: "मम", iast: "mama", english: "my / mine" },
      { sanskrit: "अन्त:करण-शुद्ध्यर्थम्", iast: "antaḥkaraṇa-śuddhyarthaṃ", english: "for the cleansing of the inner faculties / subconscious" },
      { sanskrit: "चित्त-स्थैर्य-प्राप्त्यर्थम्", iast: "citta-sthairya-prāptyarthaṃ", english: "for the attainment of absolute mental stability" },
      { sanskrit: "मानसिक-क्लेश-निवारणार्थं च", iast: "mānasika-kleśa-nivāraṇārthaṃ ca", english: "and for the dissolving of psychological sufferings/stress" }
    ]
  },
  {
    id: "wisdom",
    name: "Spiritual Wisdom & Learning",
    sanskritName: "ज्ञानं तथा विद्याप्राप्तिः",
    iastName: "Jñānaṃ tathā vidyāprāptiḥ",
    meaning: "Awakening higher intellect, deepening study (Svadhyaya), and acquiring sacred knowledge.",
    sanskritSankalpa: "मम बुद्धि-विकासार्थं ऋतम्भरा-प्रज्ञा-जागरणार्थं अध्यात्म-ज्ञान-सिद्धयर्थं च",
    iastSankalpa: "mama buddhi-vikāsārthaṃ ṛtambharā-prajñā-jāgaraṇārthaṃ adhyātma-jñāna-siddhayarthaṃ ca",
    englishSankalpa: "for the expansion of my intellect, the awakening of truth-bearing intuition, and the realization of self-contained spiritual knowledge",
    phrases: [
      { sanskrit: "मम", iast: "mama", english: "my" },
      { sanskrit: "बुद्धि-विकासार्थम्", iast: "buddhi-vikāsārthaṃ", english: "for the illumination and expansion of intellect" },
      { sanskrit: "ऋतम्भरा-प्रज्ञा", iast: "ṛtambharā-prajñā", english: "truth-bearing / absolute intuition" },
      { sanskrit: "जागरणार्थम्", iast: "jāgaraṇārthaṃ", english: "for the awakening" },
      { sanskrit: "अध्यात्म-ज्ञान-सिद्धयर्थं च", iast: "adhyātma-jñāna-siddhayarthaṃ ca", english: "and for the accomplishment of spiritual wisdom" }
    ]
  },
  {
    id: "pitri",
    name: "Ancestral Peace & Harmony",
    sanskritName: "पितृ-मोक्षः तथा वंश-शान्तिः",
    iastName: "Pitr̥-mokṣaḥ tathā vaṃśa-śāntiḥ",
    meaning: "Sending positive vibrations to departed ancestors and healing transgenerational patterns.",
    sanskritSankalpa: "मम पूर्वज-पितृणां सद्गति-प्राप्त्यर्थं वंश-परम्परा-विशुद्ध्यर्थं च",
    iastSankalpa: "mama pūrvaja-pitṛṇāṃ sadgati-prāptyarthaṃ vaṃśa-paramparā-viśuddhyarthaṃ ca",
    englishSankalpa: "for the elevated spiritual path and liberation of my departed ancestors, and for the purification of my family lineage",
    phrases: [
      { sanskrit: "मम", iast: "mama", english: "my" },
      { sanskrit: "पूर्वज-पितृणाम्", iast: "pūrvaja-pitṛṇāṃ", english: "departed forefathers and ancestors" },
      { sanskrit: "सद्गति-प्राप्त्यर्थम्", iast: "sadgati-prāptyarthaṃ", english: "for the attainment of higher spiritual realms / peace" },
      { sanskrit: "वंश-परम्परा", iast: "vaṃśa-paramparā", english: "lineage and transgenerational inheritance" },
      { sanskrit: "विशुद्ध्यर्थं च", iast: "viśuddhyarthaṃ ca", english: "and for the absolute cleansing/healing" }
    ]
  },
  {
    id: "lokasangraha",
    name: "Global Peace & Eco-Harmony",
    sanskritName: "विश्वकल्याणं तथा लोकसङ्ग्रहः",
    iastName: "Viśvakalyāṇaṃ tathā lokasaṅgrahaḥ",
    meaning: "Broadcasting selfless compassion, planetary peace, and ecological balance (Loka Samasta).",
    sanskritSankalpa: "समस्त-संसारस्य प्राणिरक्षार्थं विश्व-शान्ति-स्थापनाय भूत-हित-सिद्ध्यर्थं च",
    iastSankalpa: "samasta-saṃsārasya prāṇirakṣārthaṃ viśva-śānti-sthāpanāya bhūta-hita-siddhyarthaṃ ca",
    englishSankalpa: "for the protection and welfare of all living beings in the cosmos, the establishment of world peace, and the absolute harmony of nature",
    phrases: [
      { sanskrit: "समस्त-संसारस्य", iast: "samasta-saṃsārasya", english: "of the entire cosmos" },
      { sanskrit: "प्राणिरक्षार्थम्", iast: "prāṇirakṣārthaṃ", english: "for the protection and preservation of life" },
      { sanskrit: "विश्व-शान्ति", iast: "viśva-śānti", english: "world peace / universal harmony" },
      { sanskrit: "स्थापनाय", iast: "sthāpanāya", english: "for the establishment of" },
      { sanskrit: "भूत-हित-सिद्ध्यर्थं च", iast: "bhūta-hita-siddhyarthaṃ ca", english: "and for the well-being of all elements/nature" }
    ]
  }
];

// Inspiring Shastric Quotes for Daily Study (Brahma Yajna)
interface VedicQuote {
  sanskrit: string;
  iast: string;
  translation: string;
  source: string;
}

const VEDIC_QUOTES: VedicQuote[] = [
  {
    sanskrit: "सत्यं वद। धर्मं चर। स्वाध्यायान्मा प्रमदः।",
    iast: "satyaṃ vada | dharmaṃ cara | svādhyāyānmā pramadaḥ |",
    translation: "Speak the truth. Conduct yourself with integrity. Never neglect self-study and learning.",
    source: "Taittirīya Upaniṣad 1.11.1"
  },
  {
    sanskrit: "तेजस्वि नावधीतमस्तु मा विद्विषावहै॥",
    iast: "tejasvi nāvadhītamasatu mā vidviṣāvahai ||",
    translation: "May our learning be radiant and filled with vigor. May we never harbor resentment or division.",
    source: "Kaṭha Upaniṣad Peace Invocation"
  },
  {
    sanskrit: "ईशा वास्यमिदं सर्वं यत्किञ्च जगत्यां जगत्।",
    iast: "īśā vāsyamidaṃ sarvaṃ yatkiñca jagatyāṃ jagat |",
    translation: "All this universe—everything that moves and breathes—is enveloped in one supreme divine intelligence.",
    source: "Īśa Upaniṣad 1"
  },
  {
    sanskrit: "उदारचरितानां तु वसुधैव कुटुम्बकम्॥",
    iast: "udāracaritānāṃ tu vasudhaiva kuṭumbakam ||",
    translation: "For those of noble character and open hearts, the entire earth is indeed one single family.",
    source: "Mahā Upaniṣad 6.71"
  },
  {
    sanskrit: "एकं सद्विप्रा बहुधा वदन्ति।",
    iast: "ekaṃ sadviprā bahudhā vadanti |",
    translation: "Truth is One; though the wise speak of it in various ways.",
    source: "Rigveda 1.164.46"
  }
];

export default function RitualAssistant() {
  const [activeSubTab, setActiveSubTab] = useState<"sankalpa" | "estimator" | "pancha" | "notes">("sankalpa");

  // === 1. Sankalpa State ===
  const [userName, setUserName] = useState("Ahaṃ (Myself)");
  const [userGotra, setUserGotra] = useState("Kāśyapa (Universal Gotra)");
  const [userRegion, setUserRegion] = useState("Western Hemisphere");
  const [selectedIntention, setSelectedIntention] = useState<SankalpaIntention>(INTENTIONS[0]);
  const [copied, setCopied] = useState(false);

  // === 2. Estimator State ===
  const [ahutis, setAhutis] = useState(21);
  const [participants, setParticipants] = useState(1);

  // === 3. Pancha Yajna State ===
  const [panchaState, setPanchaState] = useState<{ [key: string]: boolean }>(() => {
    try {
      const saved = localStorage.getItem("vedic_pancha_yajna");
      return saved ? JSON.parse(saved) : { brahma: false, deva: false, pitri: false, manushya: false, bhuta: false };
    } catch {
      return { brahma: false, deva: false, pitri: false, manushya: false, bhuta: false };
    }
  });

  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

  useEffect(() => {
    localStorage.setItem("vedic_pancha_yajna", JSON.stringify(panchaState));
  }, [panchaState]);

  // Rotates daily quote
  const rotateQuote = () => {
    setCurrentQuoteIndex((prev) => (prev + 1) % VEDIC_QUOTES.length);
  };

  const handleTogglePancha = (key: string) => {
    setPanchaState(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetPanchaDaily = () => {
    setPanchaState({ brahma: false, deva: false, pitri: false, manushya: false, bhuta: false });
  };

  // Calculate completion percentage
  const completedCount = Object.values(panchaState).filter(Boolean).length;

  // === 4. Practice Notes State & Log Synchronization ===
  const [history, setHistory] = useState<PracticeSession[]>(() => {
    try {
      const saved = localStorage.getItem("vedic_history");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [selectedSessionId, setSelectedSessionId] = useState<string>("");
  const [noteText, setNoteText] = useState<string>("");
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);

  // Sync with App history changes
  useEffect(() => {
    const handleHistorySync = () => {
      try {
        const saved = localStorage.getItem("vedic_history");
        if (saved) {
          const parsed = JSON.parse(saved);
          setHistory(parsed);
          // If the selected session is no longer in the history, reset it
          if (selectedSessionId && !parsed.some((s: PracticeSession) => s.id === selectedSessionId)) {
            setSelectedSessionId("");
            setNoteText("");
          }
        } else {
          setHistory([]);
          setSelectedSessionId("");
          setNoteText("");
        }
      } catch (e) {
        console.error("Failed to sync history in RitualAssistant", e);
      }
    };
    window.addEventListener("vedic-history-updated", handleHistorySync);
    return () => window.removeEventListener("vedic-history-updated", handleHistorySync);
  }, [selectedSessionId]);

  // Load note text when selected session changes
  useEffect(() => {
    if (selectedSessionId) {
      const selected = history.find(s => s.id === selectedSessionId);
      if (selected) {
        setNoteText(selected.notes || "");
      }
    } else if (history.length > 0) {
      // Default to the most recent practice session if nothing is selected yet
      setSelectedSessionId(history[0].id);
      setNoteText(history[0].notes || "");
    }
  }, [selectedSessionId, history]);

  const handleSaveSessionNote = () => {
    if (!selectedSessionId) {
      if (noteText.trim()) {
        handleCreateGeneralNote(noteText);
      }
      return;
    }

    const updatedHistory = history.map((session) => {
      if (session.id === selectedSessionId) {
        return { ...session, notes: noteText };
      }
      return session;
    });

    setHistory(updatedHistory);
    localStorage.setItem("vedic_history", JSON.stringify(updatedHistory));
    try { window.dispatchVedicEvent?.("vedic-history-updated"); } catch(e){}
    try { window.dispatchVedicEvent?.("vedic-steps-updated"); } catch(e){}

    setSaveFeedback("Practice note successfully updated!");
    setTimeout(() => setSaveFeedback(null), 3000);
  };

  const handleCreateGeneralNote = (text: string) => {
    if (!text.trim()) return;
    const newSession: PracticeSession = {
      id: "general_" + Date.now(),
      date: new Date().toLocaleDateString("en-NP", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      }),
      mantraId: "general_sadhana",
      mantraTitle: "Sādhanā Observation",
      score: 100,
      pitchMatches: 100,
      durationSeconds: 0,
      notes: text
    };
    const updatedHistory = [newSession, ...history];
    setHistory(updatedHistory);
    localStorage.setItem("vedic_history", JSON.stringify(updatedHistory));
    try { window.dispatchVedicEvent?.("vedic-history-updated"); } catch(e){}
    try { window.dispatchVedicEvent?.("vedic-steps-updated"); } catch(e){}

    setSelectedSessionId(newSession.id);
    setNoteText(text);
    setSaveFeedback("General observation recorded!");
    setTimeout(() => setSaveFeedback(null), 3000);
  };
  const completionPercentage = Math.round((completedCount / 5) * 100);

  // === Dynamic Sankalpa Generation ===
  const getSankalpaText = () => {
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-US", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    
    return {
      sanskrit: `ॐ विष्णुर्विष्णुर्विष्णुः। अद्य कलि-युगे वैवस्वत-मन्वन्तरे अमुक-क्षेत्रे (${userRegion}) शुभ-दिवसे मम गोत्रस्य (${userGotra}) अमुक-नाम-प्रवरस्य (${userName}) ${selectedIntention.sanskritSankalpa} संकल्पं करिषये। तत्-सिद्धिरस्तु।`,
      iast: `oṃ viṣṇur-viṣṇur-viṣṇuḥ | adya kali-yuge vaivasvata-manvantare amuka-kṣetre (${userRegion}) śubha-divase mama gotrasya (${userGotra}) amuka-nāma-pravarasya (${userName}) ${selectedIntention.iastSankalpa} saṅkalpaṃ kariṣye | tat-siddhirastu |`,
      english: `Om. Salutations to the all-pervading divine presence. Today, in this present era, upon this sacred landscape of ${userRegion}, on this auspicious day, I (${userName}) of the ${userGotra} family lineage, hereby formulates this solemn intention: ${selectedIntention.englishSankalpa}. May this vow be successfully accomplished!`
    };
  };

  const currentSankalpa = getSankalpaText();

  const handleCopySankalpa = () => {
    navigator.clipboard.writeText(`${currentSankalpa.sanskrit}\n\n${currentSankalpa.iast}\n\n${currentSankalpa.english}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // === Samagri Calculation Formulas ===
  // Materials required per offering (Ahuti):
  // Ghee: 3.5 ml per ahuti
  // Havan Samagri (herbs): 4 grams per ahuti
  // Samidha (firewood): 1 stick (approx 15g) per 5 ahutis, plus base wood of 150g per participant
  // Camphor: 1 block per participant for initial lighting, plus 1 block for light-up
  // Akshata (Sacred Rice): 0.5g per ahuti
  // Black Sesame: 1g per ahuti (especially if doing Pitri elements)
  
  const woodGrams = Math.round((ahutis * 4) + (participants * 180));
  const gheeMl = Math.round(ahutis * 3.5 + 20);
  const herbsGrams = Math.round(ahutis * 3.5);
  const camphorBlocks = Math.round(participants + 2);
  const riceGrams = Math.round(ahutis * 0.5 + 10);
  const sesameGrams = Math.round(ahutis * 0.8);

  return (
    <div id="ritual-assistant-panel" className="bg-neutral-900 border border-neutral-800 rounded-2xl shadow-xl overflow-hidden relative">
      {/* Visual Accent header */}
      <div className="bg-gradient-to-r from-amber-600/10 via-orange-500/10 to-amber-700/5 px-6 py-5 border-b border-neutral-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
            <h3 className="text-base font-bold text-neutral-100 font-sans tracking-tight">Karmakāṇḍa Sahāyaka</h3>
          </div>
          <p className="text-xs text-neutral-400 mt-0.5">
            Scholarly tools to assist intent, materials, and daily obligations with ease.
          </p>
        </div>

        {/* Dashboard sub-tab switchers */}
        <div className="flex bg-neutral-950/80 border border-neutral-800/80 p-1 rounded-xl gap-1">
          <button
            onClick={() => setActiveSubTab("sankalpa")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
              activeSubTab === "sankalpa"
                ? "bg-amber-500/10 border border-amber-500/25 text-amber-400"
                : "text-neutral-400 hover:text-neutral-250 hover:bg-neutral-800/50"
            }`}
          >
            <Calendar className="w-3.5 h-3.5" />
            Sankalpa (Intent)
          </button>
          <button
            onClick={() => setActiveSubTab("estimator")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
              activeSubTab === "estimator"
                ? "bg-amber-500/10 border border-amber-500/25 text-amber-400"
                : "text-neutral-400 hover:text-neutral-250 hover:bg-neutral-800/50"
            }`}
          >
            <Scale className="w-3.5 h-3.5" />
            Sāmagrī Estimator
          </button>
          <button
            onClick={() => setActiveSubTab("pancha")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
              activeSubTab === "pancha"
                ? "bg-amber-500/10 border border-amber-500/25 text-amber-400"
                : "text-neutral-400 hover:text-neutral-250 hover:bg-neutral-800/50"
            }`}
          >
            <CheckSquare className="w-3.5 h-3.5" />
            Pancha Yajña
          </button>
          <button
            onClick={() => setActiveSubTab("notes")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
              activeSubTab === "notes"
                ? "bg-amber-500/10 border border-amber-500/25 text-amber-400"
                : "text-neutral-400 hover:text-neutral-250 hover:bg-neutral-800/50"
            }`}
          >
            <ClipboardList className="w-3.5 h-3.5" />
            Practice Notes
          </button>
        </div>
      </div>

      <div className="p-6">
        <AnimatePresence mode="wait">
          {/* ==================== SUB-TAB 1: SANKALPA BUILDER ==================== */}
          {activeSubTab === "sankalpa" && (
            <motion.div
              key="sankalpa-tab"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-4 flex gap-3">
                <Compass className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold text-amber-400">What is a Sankalpa (सङ्कल्प)?</h4>
                  <p className="text-[11px] text-neutral-300 leading-relaxed mt-1">
                    A Sankalpa is the starting coordinate of any Vedic ritual. It is a declaration of presence, mapping your name, lineage (Gotra), physical location, and spiritual purpose, aligning your mind into a single point of focused light before any mantra is spoken.
                  </p>
                </div>
              </div>

              {/* Form Controls */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block mb-1">Your Name (नाम)</label>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-xs text-neutral-250 outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block mb-1">Ancestral Family / Gotra (गोत्र)</label>
                  <input
                    type="text"
                    value={userGotra}
                    onChange={(e) => setUserGotra(e.target.value)}
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-xs text-neutral-250 outline-none focus:border-amber-500"
                    placeholder="e.g. Kashyapa, Shandilya"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block mb-1">Location Zone (देश/क्षेत्र)</label>
                  <input
                    type="text"
                    value={userRegion}
                    onChange={(e) => setUserRegion(e.target.value)}
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-xs text-neutral-250 outline-none focus:border-amber-500"
                  />
                </div>
              </div>

              {/* Selection of Intentions */}
              <div>
                <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block mb-2">Select Spiritual Focus (संकल्प-प्रयोजन)</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2.5">
                  {INTENTIONS.map((intent) => (
                    <button
                      key={intent.id}
                      onClick={() => setSelectedIntention(intent)}
                      className={`p-3 rounded-xl border text-left transition duration-200 cursor-pointer ${
                        selectedIntention.id === intent.id
                          ? "bg-amber-500/10 border-amber-500/40 text-amber-400"
                          : "bg-neutral-950 hover:bg-neutral-900 border-neutral-800 text-neutral-300"
                      }`}
                    >
                      <span className="text-xs font-bold block mb-1">{intent.name}</span>
                      <span className="text-[9px] text-neutral-400 font-mono block leading-normal line-clamp-2">{intent.sanskritName}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Intention Details */}
              <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-4">
                <h5 className="text-[11px] font-bold text-amber-500 flex items-center gap-1.5 uppercase tracking-wide">
                  <Heart className="w-3.5 h-3.5" /> Chosen Intention: {selectedIntention.name}
                </h5>
                <p className="text-[11px] text-neutral-400 mt-1 leading-normal">
                  {selectedIntention.meaning}
                </p>
              </div>

              {/* Interactive Scroll Container */}
              <div className="bg-gradient-to-b from-amber-950/20 to-neutral-950 border border-amber-900/30 rounded-2xl p-5 md:p-6 relative">
                {/* Scroll handle accents */}
                <div className="absolute top-0 bottom-0 left-0 w-1 bg-amber-500/20 rounded-l" />
                <div className="flex items-center justify-between border-b border-neutral-800 pb-3 mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-500 font-mono">Formal Sacred Scroll (सङ्कल्प-मन्त्र)</span>
                  <button
                    onClick={handleCopySankalpa}
                    className="text-[10px] bg-neutral-900 hover:bg-neutral-850 text-neutral-300 border border-neutral-800 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 cursor-pointer transition"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-emerald-400 font-semibold">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Clipboard className="w-3.5 h-3.5" />
                        <span>Copy Vow</span>
                      </>
                    )}
                  </button>
                </div>

                {/* The Sankalpa Texts */}
                <div className="space-y-4">
                  <div>
                    <span className="text-[9px] text-neutral-500 font-mono uppercase block mb-1">Sanskrit (Devanāgarī)</span>
                    <p className="text-sm md:text-base font-semibold text-amber-200 leading-relaxed font-sans select-all">
                      {currentSankalpa.sanskrit}
                    </p>
                  </div>

                  <div>
                    <span className="text-[9px] text-neutral-500 font-mono uppercase block mb-1">IAST Transliteration</span>
                    <p className="text-xs md:text-sm font-medium text-amber-400/90 leading-relaxed font-mono select-all">
                      {currentSankalpa.iast}
                    </p>
                  </div>

                  <div className="border-t border-neutral-800/60 pt-3">
                    <span className="text-[9px] text-neutral-500 font-mono uppercase block mb-1">English Translation & Intended Meaning</span>
                    <p className="text-xs text-neutral-300 leading-relaxed italic">
                      "{currentSankalpa.english}"
                    </p>
                  </div>

                  {/* Scholarly breakdown breakdown */}
                  <div className="border-t border-neutral-850 pt-4">
                    <span className="text-[9px] text-amber-500/80 font-bold uppercase block mb-2 tracking-wider">Bite-Sized Grammatical Decomposition:</span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {selectedIntention.phrases.map((ph, idx) => (
                        <div key={idx} className="bg-neutral-950/40 border border-neutral-850/80 rounded-lg p-2 flex flex-col justify-between">
                          <span className="text-[11px] font-bold text-amber-200">{ph.sanskrit}</span>
                          <span className="text-[9px] text-amber-500/85 font-mono italic my-0.5">{ph.iast}</span>
                          <span className="text-[9px] text-neutral-400 leading-tight">{ph.english}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ==================== SUB-TAB 2: SAMAGRI ESTIMATOR ==================== */}
          {activeSubTab === "estimator" && (
            <motion.div
              key="estimator-tab"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-4 flex gap-3">
                <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold text-amber-400">Perfect Sacred Proportions (सामग्री-मान)</h4>
                  <p className="text-[11px] text-neutral-300 leading-relaxed mt-1">
                    Vedic yajna rules prescribe highly specific ratios of wood, clarified butter (ghee), and protective medicinal herbs to ensure complete combustion without excess carbon soot. Use this calculator to estimate the standard ingredients you should prepare for a clean, hazard-free session.
                  </p>
                </div>
              </div>

              {/* Controls Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-neutral-950 border border-neutral-800 rounded-2xl p-6">
                {/* Left Side: Sliders */}
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-neutral-200 flex items-center gap-1.5">
                        <Flame className="w-4 h-4 text-orange-500" />
                        Number of Offerings (आहुति संख्या)
                      </span>
                      <span className="text-xs font-mono font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-0.5">
                        {ahutis} chants
                      </span>
                    </div>
                    <input
                      type="range"
                      min={11}
                      max={108}
                      step={1}
                      value={ahutis}
                      onChange={(e) => setAhutis(Number(e.target.value))}
                      className="w-full accent-amber-500 cursor-pointer h-1.5 bg-neutral-800 rounded-lg"
                    />
                    <div className="flex items-center justify-between text-[9px] text-neutral-500 font-mono mt-1">
                      <span>11 (Basic)</span>
                      <span>21 (Intermediate)</span>
                      <span>54 (Full Segment)</span>
                      <span>108 (Complete Circle)</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-neutral-200 flex items-center gap-1.5">
                        <Users className="w-4 h-4 text-amber-500" />
                        Number of Participants (यजमान)
                      </span>
                      <span className="text-xs font-mono font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-0.5">
                        {participants} {participants === 1 ? "person" : "people"}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={8}
                      step={1}
                      value={participants}
                      onChange={(e) => setParticipants(Number(e.target.value))}
                      className="w-full accent-amber-500 cursor-pointer h-1.5 bg-neutral-800 rounded-lg"
                    />
                    <div className="flex items-center justify-between text-[9px] text-neutral-500 font-mono mt-1">
                      <span>1 (Solo)</span>
                      <span>2 (Dampati / Couple)</span>
                      <span>4 (Small Gathering)</span>
                      <span>8 (Assembly)</span>
                    </div>
                  </div>
                </div>

                {/* Right Side: Quick facts */}
                <div className="border border-neutral-800 bg-neutral-900/40 rounded-xl p-4 flex flex-col justify-between">
                  <h5 className="text-[10px] font-bold uppercase text-amber-500 tracking-wider mb-2 font-mono">Ecological Tip for Western Practitioners</h5>
                  <p className="text-[11px] text-neutral-400 leading-relaxed mb-3">
                    If dried Mango wood (the standard scriptural wood) is unavailable in your country, dry Cedar, Sandalwood, Pine, or organic dry coconut husk shell makes a highly effective and pleasant substitute! Avoid treated, varnished, or artificial craft wood as it emits harmful toxic compounds.
                  </p>
                  <div className="text-[10px] bg-amber-500/5 text-amber-300 rounded-lg p-2.5 border border-amber-500/10 font-mono">
                    <strong>Total Duration:</strong> approx {Math.round(ahutis * 0.4 + 10)} mins
                  </div>
                </div>
              </div>

              {/* Estimator Display Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Wood */}
                <div className="bg-neutral-950 border border-neutral-850 rounded-2xl p-4 flex flex-col justify-between hover:border-neutral-800 transition">
                  <div>
                    <span className="text-[9px] text-neutral-500 font-mono uppercase block">Samidhā / Firewood</span>
                    <span className="text-lg md:text-xl font-bold text-amber-400 font-mono mt-1 block">~ {woodGrams}g</span>
                  </div>
                  <span className="text-[9px] text-neutral-400 leading-normal mt-3">
                    Dry twigs/wood sticks of mango, cedar or pine.
                  </span>
                </div>

                {/* Ghee */}
                <div className="bg-neutral-950 border border-neutral-850 rounded-2xl p-4 flex flex-col justify-between hover:border-neutral-800 transition">
                  <div>
                    <span className="text-[9px] text-neutral-500 font-mono uppercase block">Ājya / Clarified Butter</span>
                    <span className="text-lg md:text-xl font-bold text-amber-400 font-mono mt-1 block">~ {gheeMl} ml</span>
                  </div>
                  <span className="text-[9px] text-neutral-400 leading-normal mt-3">
                    Pure grass-fed ghee (melted), poured with a small spoon.
                  </span>
                </div>

                {/* Herbal mixture */}
                <div className="bg-neutral-950 border border-neutral-850 rounded-2xl p-4 flex flex-col justify-between hover:border-neutral-800 transition">
                  <div>
                    <span className="text-[9px] text-neutral-500 font-mono uppercase block">Havan Sāmagrī / Herbs</span>
                    <span className="text-lg md:text-xl font-bold text-amber-400 font-mono mt-1 block">~ {herbsGrams}g</span>
                  </div>
                  <span className="text-[9px] text-neutral-400 leading-normal mt-3">
                    Organic herbal mixture (with guggulu, sandalwood, camphor).
                  </span>
                </div>

                {/* Camphor */}
                <div className="bg-neutral-950 border border-neutral-850 rounded-2xl p-4 flex flex-col justify-between hover:border-neutral-800 transition">
                  <div>
                    <span className="text-[9px] text-neutral-500 font-mono uppercase block">Karpūra / Camphor</span>
                    <span className="text-lg md:text-xl font-bold text-amber-400 font-mono mt-1 block">{camphorBlocks} blocks</span>
                  </div>
                  <span className="text-[9px] text-neutral-400 leading-normal mt-3">
                    Placed at the base for clean and smokeless ignition.
                  </span>
                </div>

                {/* Unbroken Rice */}
                <div className="bg-neutral-950 border border-neutral-850 rounded-2xl p-4 flex flex-col justify-between hover:border-neutral-800 transition">
                  <div>
                    <span className="text-[9px] text-neutral-500 font-mono uppercase block">Akṣata / White Rice</span>
                    <span className="text-lg md:text-xl font-bold text-amber-400 font-mono mt-1 block">~ {riceGrams}g</span>
                  </div>
                  <span className="text-[9px] text-neutral-400 leading-normal mt-3">
                    Whole uncooked white rice grains mixed with turmeric.
                  </span>
                </div>

                {/* Black Sesame */}
                <div className="bg-neutral-950 border border-neutral-850 rounded-2xl p-4 flex flex-col justify-between hover:border-neutral-800 transition">
                  <div>
                    <span className="text-[9px] text-neutral-500 font-mono uppercase block">Kāla Tila / Black Sesame</span>
                    <span className="text-lg md:text-xl font-bold text-amber-400 font-mono mt-1 block">~ {sesameGrams}g</span>
                  </div>
                  <span className="text-[9px] text-neutral-400 leading-normal mt-3">
                    Highly essential if incorporating ancestral water offerings.
                  </span>
                </div>
              </div>

              {/* Ritual Checklist */}
              <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-4">
                <span className="text-[10px] font-bold text-amber-500 font-mono uppercase tracking-wider block mb-2">Essential Physical Equipment Checklist:</span>
                <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-[11px] text-neutral-300">
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Fire Pot (Havan Kund/metal pan)</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Achamana Cup (Pancha-patra & Spoon)</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Matches / Safe spark lighter</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Wooden spoon/tong (Sruva)</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Small towels or paper wipes</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Clean plate/tray to hold herbs</li>
                </ul>
              </div>
            </motion.div>
          )}

          {/* ==================== SUB-TAB 3: PANCHA MAHA YAJNA ==================== */}
          {activeSubTab === "pancha" && (
            <motion.div
              key="pancha-tab"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-12 gap-6"
            >
              {/* Daily Tracker Ring (Column 4) */}
              <div className="md:col-span-4 flex flex-col items-center justify-center bg-neutral-950 border border-neutral-800 rounded-2xl p-6 text-center">
                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 font-mono mb-4">Daily Cosmic Rhythm</span>
                
                {/* Circular Progress SVG */}
                <div className="relative w-28 h-28 flex items-center justify-center">
                  <svg className="absolute inset-0 w-full h-full -rotate-90">
                    <circle
                      cx="56"
                      cy="56"
                      r="46"
                      className="stroke-neutral-850 fill-transparent"
                      strokeWidth="6"
                    />
                    <circle
                      cx="56"
                      cy="56"
                      r="46"
                      className="stroke-amber-500 transition-all duration-500 fill-transparent"
                      strokeWidth="6"
                      strokeDasharray="289"
                      strokeDashoffset={289 - (289 * completionPercentage) / 100}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="flex flex-col items-center justify-center">
                    <span className="text-2xl font-black text-amber-400 font-mono">{completionPercentage}%</span>
                    <span className="text-[9px] text-neutral-400 mt-0.5">Harmonized</span>
                  </div>
                </div>

                <div className="mt-4">
                  <span className="text-xs font-bold text-neutral-200 block">{completedCount} of 5 Completed</span>
                  <span className="text-[10px] text-neutral-500 mt-1 block italic max-w-xs leading-normal">
                    "When the individual aligns with the cosmos, prosperity (Dharma) naturally manifests."
                  </span>
                </div>

                {completedCount > 0 && (
                  <button
                    onClick={resetPanchaDaily}
                    className="text-[10px] text-amber-500/80 hover:text-amber-400 mt-4 underline cursor-pointer"
                  >
                    Reset Daily Tracker
                  </button>
                )}
              </div>

              {/* Checklist Items (Column 8) */}
              <div className="md:col-span-8 space-y-4">
                <div className="flex items-center justify-between border-b border-neutral-850 pb-2">
                  <span className="text-[11px] font-bold text-amber-500 font-mono uppercase tracking-wider">The 5 Great Duties (दैनिक-पञ्चमहायज्ञ)</span>
                  <span className="text-[10px] text-neutral-400 leading-none">Tap to toggle completion</span>
                </div>

                <div className="space-y-3">
                  {/* 1. Brahma Yajna */}
                  <div
                    onClick={() => handleTogglePancha("brahma")}
                    className={`p-3.5 border rounded-xl flex items-start gap-3 transition cursor-pointer select-none ${
                      panchaState.brahma
                        ? "bg-amber-500/5 border-amber-500/25"
                        : "bg-neutral-950/60 hover:bg-neutral-900 border-neutral-850"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={panchaState.brahma}
                      readOnly
                      className="accent-amber-500 w-4 h-4 rounded border-neutral-800 mt-0.5 pointer-events-none"
                    />
                    <div>
                      <h6 className={`text-xs font-bold flex items-center gap-1.5 ${panchaState.brahma ? "text-amber-300" : "text-neutral-200"}`}>
                        <BookOpen className="w-3.5 h-3.5" />
                        Brahma Yajña (Svadhyāya / Daily Wisdom)
                      </h6>
                      <p className="text-[10px] text-neutral-400 leading-normal mt-0.5">
                        Honoring the ancient sages. Spend 60 seconds reading or studying sacred verses or reviewing Sanskrit grammar. See below for your daily verse!
                      </p>
                    </div>
                  </div>

                  {/* 2. Deva Yajna */}
                  <div
                    onClick={() => handleTogglePancha("deva")}
                    className={`p-3.5 border rounded-xl flex items-start gap-3 transition cursor-pointer select-none ${
                      panchaState.deva
                        ? "bg-amber-500/5 border-amber-500/25"
                        : "bg-neutral-950/60 hover:bg-neutral-900 border-neutral-850"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={panchaState.deva}
                      readOnly
                      className="accent-amber-500 w-4 h-4 rounded border-neutral-800 mt-0.5 pointer-events-none"
                    />
                    <div>
                      <h6 className={`text-xs font-bold flex items-center gap-1.5 ${panchaState.deva ? "text-amber-300" : "text-neutral-200"}`}>
                        <Flame className="w-3.5 h-3.5" />
                        Deva Yajña (Sacred Light / Offerings)
                      </h6>
                      <p className="text-[10px] text-neutral-400 leading-normal mt-0.5">
                        Honoring cosmic forces. Light a ghee candle, incense, or complete at least 3 mantras on our virtual pitch visualizer.
                      </p>
                    </div>
                  </div>

                  {/* 3. Pitri Yajna */}
                  <div
                    onClick={() => handleTogglePancha("pitri")}
                    className={`p-3.5 border rounded-xl flex items-start gap-3 transition cursor-pointer select-none ${
                      panchaState.pitri
                        ? "bg-amber-500/5 border-amber-500/25"
                        : "bg-neutral-950/60 hover:bg-neutral-900 border-neutral-850"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={panchaState.pitri}
                      readOnly
                      className="accent-amber-500 w-4 h-4 rounded border-neutral-800 mt-0.5 pointer-events-none"
                    />
                    <div>
                      <h6 className={`text-xs font-bold flex items-center gap-1.5 ${panchaState.pitri ? "text-amber-300" : "text-neutral-200"}`}>
                        <Droplet className="w-3.5 h-3.5" />
                        Pitr̥ Yajña (Ancestral Gratitude)
                      </h6>
                      <p className="text-[10px] text-neutral-400 leading-normal mt-0.5">
                        Honoring parents and lineages. Pour a glass of water into a houseplant/soil, or take 10 seconds of silence to recall and thank your parents and lineage ancestors.
                      </p>
                    </div>
                  </div>

                  {/* 4. Manushya Yajna */}
                  <div
                    onClick={() => handleTogglePancha("manushya")}
                    className={`p-3.5 border rounded-xl flex items-start gap-3 transition cursor-pointer select-none ${
                      panchaState.manushya
                        ? "bg-amber-500/5 border-amber-500/25"
                        : "bg-neutral-950/60 hover:bg-neutral-900 border-neutral-850"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={panchaState.manushya}
                      readOnly
                      className="accent-amber-500 w-4 h-4 rounded border-neutral-800 mt-0.5 pointer-events-none"
                    />
                    <div>
                      <h6 className={`text-xs font-bold flex items-center gap-1.5 ${panchaState.manushya ? "text-amber-300" : "text-neutral-200"}`}>
                        <Heart className="w-3.5 h-3.5" />
                        Manuṣya Yajña (Humanitarian Aid & Hospitality)
                      </h6>
                      <p className="text-[10px] text-neutral-400 leading-normal mt-0.5">
                        Honoring human society. Offer a kind word, donate to a cause, cook a meal for a loved one, or perform a selfless favor for a stranger.
                      </p>
                    </div>
                  </div>

                  {/* 5. Bhuta Yajna */}
                  <div
                    onClick={() => handleTogglePancha("bhuta")}
                    className={`p-3.5 border rounded-xl flex items-start gap-3 transition cursor-pointer select-none ${
                      panchaState.bhuta
                        ? "bg-amber-500/5 border-amber-500/25"
                        : "bg-neutral-950/60 hover:bg-neutral-900 border-neutral-850"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={panchaState.bhuta}
                      readOnly
                      className="accent-amber-500 w-4 h-4 rounded border-neutral-800 mt-0.5 pointer-events-none"
                    />
                    <div>
                      <h6 className={`text-xs font-bold flex items-center gap-1.5 ${panchaState.bhuta ? "text-amber-300" : "text-neutral-200"}`}>
                        <TrendingUp className="w-3.5 h-3.5" />
                        Bhūta Yajña (Compassion to Wildlife & Plants)
                      </h6>
                      <p className="text-[10px] text-neutral-400 leading-normal mt-0.5">
                        Honoring animal and vegetative ecosystems. Set a bowl of water or grains on your balcony for birds, feed a stray animal, or water some garden flowers.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Svadhyaya Daily Verse Block */}
                <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-4 mt-2">
                  <div className="flex items-center justify-between border-b border-neutral-850 pb-2 mb-3">
                    <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wide">Daily Wisdom Verse (स्वाध्याय)</span>
                    <button
                      onClick={rotateQuote}
                      className="text-[9px] text-amber-500 hover:text-amber-400 cursor-pointer flex items-center gap-0.5 font-mono"
                    >
                      Next Verse <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-amber-200 leading-normal font-sans text-center">
                      {VEDIC_QUOTES[currentQuoteIndex].sanskrit}
                    </p>
                    <p className="text-[10px] text-amber-400/90 font-mono italic text-center leading-normal">
                      {VEDIC_QUOTES[currentQuoteIndex].iast}
                    </p>
                    <p className="text-[10px] text-neutral-300 italic text-center leading-normal border-t border-neutral-900 pt-2">
                      "{VEDIC_QUOTES[currentQuoteIndex].translation}"
                    </p>
                    <div className="text-right">
                      <span className="text-[8px] text-neutral-500 font-mono">— {VEDIC_QUOTES[currentQuoteIndex].source}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ==================== SUB-TAB 4: PRACTICE NOTES ==================== */}
          {activeSubTab === "notes" && (
            <motion.div
              key="notes-tab"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {/* Informative Header Banner */}
              <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-4 flex gap-3">
                <FileText className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold text-amber-400">Ritual Journaling & Svadhyāya (स्वाध्याय-पत्रिका)</h4>
                  <p className="text-[11px] text-neutral-300 leading-relaxed mt-1">
                    Documenting throat resonance (Svara), mental focus, and ritual conditions helps cultivate traditional discipline. Record physical sensations, fire behavior (camphor/ghee balance), or cognitive shifts during your chant practices.
                  </p>
                </div>
              </div>

              {history.length === 0 ? (
                /* Empty state: allows general note creation */
                <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-6 text-center max-w-xl mx-auto space-y-4">
                  <div className="inline-flex p-3 bg-neutral-900 border border-neutral-800 rounded-full text-amber-500/70">
                    <ClipboardList className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <h5 className="text-xs font-bold text-neutral-200">No Chanting Sessions Recorded Yet</h5>
                    <p className="text-[10px] text-neutral-400 leading-normal max-w-sm mx-auto">
                      Complete a pitch-matching practice on the main dashboard to annotate structured sessions, or record a general sādhanā reflection right now below.
                    </p>
                  </div>

                  <div className="text-left border-t border-neutral-900 pt-4 space-y-3">
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block">
                      General Ritual Observation / Reflection Note
                    </label>
                    <textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="e.g., Conducted morning Agnihotra. Fire was extremely steady and clean. Felt a deep throat resonance during the concluding Svarita chanting..."
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-lg p-3 text-xs text-neutral-250 min-h-[100px] outline-none focus:border-amber-500 transition resize-none"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] text-neutral-500 font-mono">
                        Saved locally in your browser.
                      </span>
                      <button
                        onClick={() => handleCreateGeneralNote(noteText)}
                        disabled={!noteText.trim()}
                        className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-neutral-950 text-[10px] font-bold px-4 py-2 rounded-lg transition font-mono uppercase tracking-wider cursor-pointer"
                      >
                        Save Reflection
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Full state: two-column list and editor */
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                  {/* Left Column: List of sessions */}
                  <div className="md:col-span-5 space-y-3">
                    <div className="flex items-center justify-between border-b border-neutral-850 pb-2">
                      <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider font-mono">
                        Practice Session Logs
                      </span>
                      <button
                        onClick={() => {
                          setSelectedSessionId("");
                          setNoteText("");
                        }}
                        className={`text-[9px] font-mono font-bold px-2 py-1 rounded transition cursor-pointer ${
                          selectedSessionId === ""
                            ? "bg-amber-500/10 border border-amber-500/25 text-amber-400"
                            : "bg-neutral-950 border border-neutral-850 text-neutral-400 hover:text-neutral-200"
                        }`}
                      >
                        + General Note
                      </button>
                    </div>

                    <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1 custom-scrollbar">
                      {history.map((session) => {
                        const isSelected = session.id === selectedSessionId;
                        const scoreColor =
                          session.score >= 80
                            ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
                            : session.score >= 60
                            ? "text-amber-400 bg-amber-500/10 border-amber-500/20"
                            : "text-rose-400 bg-rose-500/10 border-rose-500/20";

                        return (
                          <div
                            key={session.id}
                            onClick={() => setSelectedSessionId(session.id)}
                            className={`p-3 border rounded-xl flex items-center justify-between transition cursor-pointer select-none ${
                              isSelected
                                ? "bg-amber-500/5 border-amber-500/35 shadow-md"
                                : "bg-neutral-950 hover:bg-neutral-900 border-neutral-850"
                            }`}
                          >
                            <div className="min-w-0 flex-1 pr-2">
                              <span className="text-xs font-bold text-neutral-200 block truncate">
                                {session.mantraTitle}
                              </span>
                              <span className="text-[9px] text-neutral-500 font-mono block mt-0.5">
                                {session.date}
                              </span>
                              {session.notes && (
                                <p className="text-[9px] text-amber-400/80 italic truncate mt-1">
                                  "{session.notes}"
                                </p>
                              )}
                            </div>
                            {session.score > 0 && (
                              <div className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded-md border ${scoreColor}`}>
                                {session.score}%
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right Column: Note Editor */}
                  <div className="md:col-span-7 bg-neutral-950 border border-neutral-800 rounded-2xl p-5 flex flex-col justify-between">
                    <div className="space-y-4">
                      {/* Active Session Info Header */}
                      <div className="border-b border-neutral-850 pb-3">
                        {selectedSessionId ? (
                          (() => {
                            const activeSession = history.find(s => s.id === selectedSessionId);
                            if (!activeSession) return null;

                            return (
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-bold text-neutral-200">
                                    Reflections for {activeSession.mantraTitle}
                                  </span>
                                  {activeSession.score > 0 && (
                                    <span className="text-[10px] font-mono text-neutral-400">
                                      Accuracy: <strong className="text-amber-400">{activeSession.score}%</strong>
                                    </span>
                                  )}
                                </div>
                                <div className="text-[9px] text-neutral-500 font-mono flex items-center gap-2">
                                  <span>Logged: {activeSession.date}</span>
                                  <span>•</span>
                                  <span>Pitch Match: {activeSession.pitchMatches}%</span>
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="space-y-1">
                            <span className="text-xs font-bold text-amber-400 block">
                              New General Sādhanā Reflection
                            </span>
                            <p className="text-[9px] text-neutral-500 leading-normal">
                              Record a general ritual observation or practice insight not linked to a specific voice chant.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Text Editor Area */}
                      <div className="space-y-3">
                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block">
                          Personal Observations & Svara Notes
                        </label>
                        <textarea
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="What did you observe? e.g., 'Felt strong chest vibration during Pranavah chant. Fire camphor burned completely. The pitch guidance was exceptionally helpful to stabilize Udatta accents...'"
                          className="w-full bg-neutral-900 border border-neutral-800 rounded-xl p-3 text-xs text-neutral-200 min-h-[140px] outline-none focus:border-amber-500/50 transition resize-none leading-relaxed"
                        />
                      </div>
                    </div>

                    {/* Action buttons and Toast messages */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-neutral-900 pt-4 mt-4">
                      <div className="min-h-[20px] flex items-center">
                        {saveFeedback && (
                          <motion.span
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="text-[10px] font-semibold text-emerald-400"
                          >
                            ✓ {saveFeedback}
                          </motion.span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-auto">
                        <button
                          onClick={handleSaveSessionNote}
                          className="bg-amber-500 hover:bg-amber-600 text-neutral-950 font-bold text-[10px] font-mono px-4 py-2 rounded-lg transition uppercase tracking-wider cursor-pointer"
                        >
                          {selectedSessionId ? "Update Note" : "Save General Note"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
